import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { colors } from '../styles/colors';
import { globalStyles } from '../styles/globalStyles';
import CustomButton from '../components/CustomButton';
import bookingAPI from '../services/bookingApi';

// Responsive helper functions
const getResponsiveValue = (small, medium, large) => {
  const width = window.innerWidth;
  if (width >= 1024) return large;
  if (width >= 768) return medium;
  return small;
};

const SignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      alert('Please fill in all required fields');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return false;
    }

    if (formData.password.length < 6) {
      alert('Password must be at least 6 characters long');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSignup = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await bookingAPI.userSignup({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        organization: formData.organization.trim(),
        password: formData.password
      });
      
      if (response.success) {
        alert('Account created successfully! Please log in with your credentials.');
        navigate('/');
      } else {
        alert(response.message || 'Signup failed. Please try again.');
      }
    } catch (error) {
      console.error('Signup error:', error);
      alert('Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSignup();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.scrollView}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title}>Create Account</div>
          <div style={styles.subtitle}>Join us to start booking halls for your events</div>
        </div>

        {/* Signup Form */}
        <div style={styles.formContainer}>
          <div style={styles.formCard}>
            <div style={styles.cardTitle}>User Registration</div>
            
            {/* Name Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Full Name *</label>
              <input
                type="text"
                style={styles.input}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your full name"
                autoComplete="name"
              />
            </div>

            {/* Email Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address *</label>
              <input
                type="email"
                style={styles.input}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your email address"
                autoCapitalize="none"
                autoComplete="email"
              />
            </div>

            {/* Phone Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Phone Number</label>
              <input
                type="tel"
                style={styles.input}
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your phone number"
                autoComplete="tel"
              />
            </div>

            {/* Organization Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Organization/Department</label>
              <input
                type="text"
                style={styles.input}
                value={formData.organization}
                onChange={(e) => handleInputChange('organization', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your organization or department"
                autoComplete="organization"
              />
            </div>

            {/* Password Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password *</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  style={styles.passwordInput}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Create a password (min 6 characters)"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  style={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <span style={styles.passwordToggleText}>
                    {showPassword ? '🙈' : '👁️'}
                  </span>
                </button>
              </div>
            </div>

            {/* Confirm Password Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm Password *</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  style={styles.passwordInput}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  style={styles.passwordToggle}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <span style={styles.passwordToggleText}>
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </span>
                </button>
              </div>
            </div>

            {/* Password Match Indicator */}
            {formData.password && formData.confirmPassword && (
              <div style={styles.passwordMatch}>
                <span style={{
                  ...styles.passwordMatchText,
                  color: formData.password === formData.confirmPassword ? colors.success : colors.danger
                }}>
                  {formData.password === formData.confirmPassword ? '✅ Passwords match' : '❌ Passwords do not match'}
                </span>
              </div>
            )}

            {/* Signup Button */}
            <CustomButton
              title={loading ? 'Creating Account...' : 'Create Account'}
              onPress={handleSignup}
              disabled={loading}
              style={styles.signupButton}
            />


            {/* Divider */}
            <div style={styles.divider}>
              <div style={styles.dividerLine}></div>
              <span style={styles.dividerText}>OR</span>
              <div style={styles.dividerLine}></div>
            </div>

            {/* Login Link */}
            <div style={styles.loginContainer}>
              <span style={styles.loginText}>Already have an account? </span>
              <Link to="/" style={styles.loginLink}>
                Sign In
              </Link>
            </div>
          </div>
        </div>

        {/* Back to Login */}
        <div style={styles.footer}>
          <CustomButton
            title="← Back to Login"
            variant="outline"
            onPress={() => navigate('/')}
            style={styles.backButton}
          />
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.background,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
    overflowY: 'auto',
  },
  header: {
    backgroundColor: colors.primary,
    padding: getResponsiveValue(24, 32, 40),
    textAlign: 'center',
  },
  title: {
    fontSize: getResponsiveValue(24, 28, 32),
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: getResponsiveValue(8, 10, 12),
  },
  subtitle: {
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.white,
    opacity: 0.9,
  },
  formContainer: {
    padding: getResponsiveValue(20, 32, 40),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  formCard: {
    ...globalStyles.card,
    width: '100%',
    maxWidth: getResponsiveValue(450, 500, 550),
    padding: getResponsiveValue(24, 32, 40),
  },
  cardTitle: {
    fontSize: getResponsiveValue(20, 24, 28),
    fontWeight: 'bold',
    color: colors.text,
    textAlign: 'center',
    marginBottom: getResponsiveValue(24, 32, 40),
  },
  inputGroup: {
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  label: {
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
    color: colors.text,
    marginBottom: getResponsiveValue(6, 8, 10),
    display: 'block',
  },
  input: {
    width: '100%',
    padding: getResponsiveValue(12, 16, 20),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(8, 10, 12),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    backgroundColor: colors.white,
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: colors.primary,
    },
  },
  passwordContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    width: '100%',
    padding: getResponsiveValue(12, 16, 20),
    paddingRight: getResponsiveValue(40, 48, 56),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(8, 10, 12),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    backgroundColor: colors.white,
    outline: 'none',
    boxSizing: 'border-box',
  },
  passwordToggle: {
    position: 'absolute',
    right: getResponsiveValue(8, 12, 16),
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: getResponsiveValue(4, 6, 8),
  },
  passwordToggleText: {
    fontSize: getResponsiveValue(16, 18, 20),
  },
  passwordMatch: {
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  passwordMatchText: {
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: '600',
  },
  signupButton: {
    width: '100%',
    marginTop: getResponsiveValue(8, 12, 16),
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    marginTop: getResponsiveValue(16, 20, 24),
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
  },
  dividerText: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: colors.textLight,
    marginLeft: getResponsiveValue(12, 16, 20),
    marginRight: getResponsiveValue(12, 16, 20),
  },
  loginContainer: {
    textAlign: 'center',
  },
  loginText: {
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.textLight,
  },
  loginLink: {
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: '600',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  footer: {
    padding: getResponsiveValue(20, 24, 32),
    textAlign: 'center',
  },
  backButton: {
    maxWidth: getResponsiveValue(200, 250, 300),
  },
};

export default SignupPage;